/* -------------------------- Design imports start -------------------------- */
import { Menu, ShareRounded, DraftsOutlined, MailOutlined } from '@mui/icons-material'
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined'
import RequestPageOutlinedIcon from '@mui/icons-material/RequestPageOutlined'
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined'
import { AppBar, Badge, IconButton, Toolbar, Tooltip, Typography } from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import { makeStyles } from '@mui/styles'
import React, { type ReactElement, useContext, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next'
import Joyride, { type Step as JoyrideStep } from 'react-joyride'
import { useHistory } from 'react-router-dom'

import logo from '../../assets/logo_white.svg'
import { ChatSystemGlobalStates } from '../../features/ChatSystem/ChatSystemGlobalStateProvider'
import ChannelLogic from '../../features/ChatSystem/components/Channel/ChannelLogic'
import { type Channel, type Message, type MessageObject } from '../../features/ChatSystem/utils/types'
import { SharepointGlobalStates } from '../../features/Sharepoint/SharepointGlobalStates'
import LogTool from '../../logger/logTools'
import { canView, isPrivileged } from '../../utils/functions'
import { type User } from '../../utils/types'
import { useUserContext } from '../../utils/context'
import Button from '../Button'
import AccountButton from '../widgets/AccountButton'
import { useJoyride } from '../widgets/Joyride'
import { isFeatureEnabled } from '../../config/featureFlags'
import { PERMISSIONS } from '../../config/permissions'
import ContractOverviewAdminPage from '../../pages/Contract/ContractOverviewAdminPage'
import { useWebsocket } from '../../utils/WebsocketProvider'
import { fetchChannels as fetchChannelsApi } from '../../features/ChatSystem/api/chatApi'
import { WebsocketMessage } from '../../api/types'
/* ------------------------- Functional imports end ------------------------- */

interface Props {
  title?: string
  showBack?: boolean
  centerTitle?: boolean
  isDekstop?: boolean
  onBackClick?: () => void
  setUser: (user: User | undefined) => void
  darkMode: boolean
  setDarkMode: (darkMode: boolean) => void
  open: boolean
  handleSideBar: () => void
}

export default function Header(props: Props): ReactElement {
  // const { socket } = useWebSocket()
  const { websocketMessage, websocketIsReady } = useWebsocket()
  const { unreadFeedbackSubmissionCount, addToFeedbackCount } = useContext(SharepointGlobalStates)
  const { unreadMessagesTotal } = useContext(ChatSystemGlobalStates)
  const { userPermissions, user } = useUserContext()

  const { t } = useTranslation()
  const log = new LogTool({ context: 'Header', enable: true, logLevel: 'warn' })
  /* -------------------------- Non state data start -------------------------- */
  const { setUser, handleSideBar } = props
  const { startJoyride, runJoyride, resetJoyride } = useJoyride()
  const useStyles = makeStyles(/*theme*/() => ({
    customBadge: {
      backgroundColor: '#eb9928',
      color: 'white',
    },
  }))
  const classes = useStyles()
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  /* ----------------------------- Flag states end ---------------------------- */
  /* ---------------------------- Data states start --------------------------- */
  const history = useHistory()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [badgeStatus_countMessages, setBadgeStatus_countMessages] = useState<number>(0)
  const [badgeStatus_countTotals, setBadgeStatus_countTotals] = useState<number>(0)

  const [joyrideSteps] = useState<(JoyrideStep & { stepIndex?: number })[]>([
    {
      target: '#chatButton',
      content: t('common:feedback.info.chatOnboarding'),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      spotlightPadding: 0,
      event: 'hover',
    },
    {
      target: '#fileSystemButton',
      title: t('common:content.label.filesystem'),
      content: t('common:feedback.info.fileSystemOnboarding'),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      spotlightPadding: 0,
      event: 'hover',
    },
    {
      target: '#requestOverviewButton',
      content: t('common:feedback.info.requestOnboarding'),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      spotlightPadding: 0,
      event: 'hover',
    },
    {
      target: '#accountButton',
      title: t('common:content.label.profile'),
      content: t('common:feedback.info.accountOnboarding'),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      spotlightPadding: 0,
      event: 'hover',
    },
    {
      content: t('common:feedback.info.mainMenuOnboarding'),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      spotlightPadding: 0,
      target: '#sideBarList',
      event: 'hover',
      disableBeacon: true,
    },
    {
      target: '#accordion-item',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      stepIndex: 1,
      spotlightPadding: 0,
      event: 'hover',
      content: t('common:feedback.info.subMenuOnboarding'),
    },
  ])

  /* ----------------------------- Data states end ---------------------------- */
  /* ---------------------------- use Effects start --------------------------- */
  const isChatFeatureEnabled = isFeatureEnabled('chat')
  const isFileFeatureEnabled = isFeatureEnabled('files')
  const isContractsFeatureEnabled = isFeatureEnabled('contracts')
  const isGuestRequestFeatureEnabled = isFeatureEnabled('guestRequests')
  const isSharepointFeatureEnabled = isFeatureEnabled('sharepoint')

  useEffect(() => {
    // Don't check messages for guest users - they don't have access to chat
    if (user?.isGuestUser) return
    void (async () => {
      await checkNewMessages()
    })()
  }, [user?.id, user?.isGuestUser, isChatFeatureEnabled])

  useEffect(() => {
    if (websocketIsReady && websocketMessage) {
      const msgType = websocketMessage.type
      log.info('Handling message of type', msgType)

      if (msgType.startsWith('sharepoint_')) {
        handleSharepointNotification(websocketMessage)
      } else if (msgType.startsWith('chat_')) {
        handleChatNotification(websocketMessage)
      }
    }
  }, [websocketMessage])
  /* ----------------------------- use Effects end ---------------------------- */
  /* ------------------------- Utility functions start ------------------------ */
  async function checkNewMessages(): Promise<void> {
    if (!user || !isChatFeatureEnabled || user.isGuestUser) return

    try {
      const response = await fetchChannelsApi({ expand: ['messages'] })
      const channels: Channel[] = response.results
      let count = 0
      channels.forEach(channel => {
        if (channel.messages.length > 0 && Array.isArray(channel.messages)) {
          channel.messages.forEach((message: Message) => {
            const messageAuthorSelf =
              typeof message.author === 'string'
                ? message.author
                : message.author?.self

            if (message.isNewMessage && messageAuthorSelf !== user.self) {
              count++
            }
          })
        }
      })
      setBadgeStatus_countMessages(count)
    } catch (error) {
      log.error('Error fetching channels: ', error)
    }
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleStartTour = (): void => {
    simulateMenuButtonClick()
    setTimeout(() => {
      localStorage.setItem('tourCompleted', 'false')
      startJoyride()
    }, 500)
  }

  const simulateMenuButtonClick = (): void => {
    const menuButton = document.querySelector('#menuButton') as HTMLButtonElement

    if (menuButton) {
      menuButton.click()

      setTimeout(() => {
        const sidebarList = document.querySelector('#accordion-item') as HTMLButtonElement

        if (sidebarList) {
          sidebarList.click()
        }
      }, 1000)
    }
  }

  const handleJoyrideCallback = (data: any): void => {
    if (data.type === 'tour:end' || data.type === 'tour:status:after') {
      resetJoyride()
    }
    if (data.type === 'step:after' && data.step.target === '#accordion-item') {
      // Wenn der Schritt "Sub menus" erreicht ist, führe eine Verzögerung durch und wechsle dann die Seite
      setTimeout(() => {
        history.push({
          pathname: '/items',
          state: {
            runJoyride: true,
          },
        })
      }, 1000) // Anpassen der Verzögerungszeit nach Bedarf
    }
  }

  const handleSharepointNotification = (msg: WebsocketMessage): void => {
    if (msg.type === 'sharepoint_feedback_submission') {
      let shareType: 'articleShares' | 'BOMShares' | 'guestRequestShares' | 'requestShares' =
        'articleShares'
      if (msg.data.is_bom_share && !msg.data.is_guest_request_share) {
        shareType = 'BOMShares'
      }
      if (!msg.data.is_bom_share && msg.data.is_guest_request_share) {
        shareType = 'guestRequestShares'
      }
      if (msg.data.is_request_share) {
        shareType = 'requestShares'
      }
      log.debug('Handling sharepoint feedback submission notification of type', shareType)
      addToFeedbackCount(1, shareType)
    }
  }

  const handleChatNotification = (msg: WebsocketMessage): void => {
    if (!user) return

    const messageObject: MessageObject = msg.data as MessageObject
    const message: any = messageObject[Object.keys(messageObject)[0]][0]
    if (msg.type === 'chat_message') {
      const messageAuthorSelf =
        typeof message.author === 'string'
          ? message.author
          : message.author?.self

      if (messageAuthorSelf !== user.self) {
        setBadgeStatus_countMessages(prevBadgeCount => {
          return prevBadgeCount + 1
        })
      }
    }
  }
  /* ------------------------- Callback functions end ------------------------- */

  const canAccessChat = user && !user.isGuestUser && isChatFeatureEnabled && (canView(userPermissions, PERMISSIONS.CHAT.CHAT) || isPrivileged(user))
  const canAccessFileSystem = user && !user.isGuestUser && isFileFeatureEnabled && (canView(userPermissions, PERMISSIONS.ITEMS.ARTICLE) || isPrivileged(user))
  const canAccessContracts = (isContractsFeatureEnabled || isGuestRequestFeatureEnabled) 
    && (canView(userPermissions, PERMISSIONS.REQUEST.REQUEST) || canView(userPermissions, PERMISSIONS.CONTRACT.CONTRACT) || isPrivileged(user))
  const canAccessSharepoint = user && !user.isGuestUser && isSharepointFeatureEnabled && (canView(userPermissions, PERMISSIONS.ITEMS.SHAREPOINT) || isPrivileged(user))
  const canAccessTour = user && !user.isGuestUser
  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <React.Fragment>
      <AppBar>
        <Toolbar style={{ height: '40px', minHeight: '10px', color: 'whitesmoke' }}>
          <a href="/" style={{ textDecoration: 'none' }}>
            <Typography onClick={() => history.push('/items')} variant="h5">
              <img
                src={logo}
                alt="assemblean logo"
                style={{ width: '130px', height: '80px', margin: 'auto', display: 'block' }}
              />
            </Typography>
          </a>
          {canAccessChat && (
            <Tooltip title={t('common:interaction.tooltip.messages')}>
              <IconButton
                id="chatButton"
                style={{ marginLeft: '15px', color: 'inherit' }}
                onClick={() => {
                  setBadgeStatus_countMessages(0)
                  history.push('/chats')
                }}
              >
                <Badge
                  badgeContent={unreadMessagesTotal}
                  max={99}
                  classes={{ badge: classes.customBadge }}
                >
                  {!(history.location.pathname === '/chats') ? (
                    <MailOutlined color="inherit" />
                  ) : (
                    <DraftsOutlined color="inherit" />
                  )}
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          {canAccessChat && (
            <ChannelLogic
              header={true}
              setBadgeStatus_countMessages={setBadgeStatus_countMessages}
            />
          )}
          {canAccessFileSystem && (
            <Tooltip title={t('common:interaction.tooltip.files')}>
              <IconButton
                id="fileSystemButton"
                style={{ color: 'inherit' }}
                onClick={() => history.push('/files')}
              >
                <FolderOpenOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
          {canAccessContracts && (
            <Tooltip title={t('common:interaction.tooltip.contracts')}>
              <IconButton
                id="requestOverviewButton"
                style={{ color: 'inherit' }}
                onClick={() => history.push('/contracts')}
              >
                <Badge
                  badgeContent={
                    !(history.location.pathname === '/contracts') ? badgeStatus_countTotals : 0
                  }
                  max={99}
                  classes={{ badge: classes.customBadge }}
                >
                  {!(history.location.pathname === '/contracts') ? (
                    <RequestPageOutlinedIcon />
                  ) : (
                    <RequestQuoteOutlinedIcon />
                  )}
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          {canAccessSharepoint && (
            <Tooltip title={t('sharepoint:content.label.shares')}>
              <IconButton
                id="sharedArticlesButton"
                style={{ color: 'inherit' }}
                onClick={() => history.push('/items/sharepoints')}
              >
                <Badge
                  badgeContent={unreadFeedbackSubmissionCount.total ?? 0}
                  max={99}
                  classes={{ badge: classes.customBadge }}
                >
                  <ShareRounded />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          {canAccessContracts && (
            <ContractOverviewAdminPage
              setBadgeStatus_countTotals={setBadgeStatus_countTotals}
              header={true}
            />
          )}
          <div style={{ marginLeft: 'auto', color: 'inherit' }}>
            {canAccessTour && (
              <Button
                id="tourButton"
                variant="outlined"
                size="small"
                sx={{ borderColor: 'whitesmoke', color: 'whitesmoke' }}
                onClick={handleStartTour}
              >
                {t('common:interaction.button.startTour')}
              </Button>
            )}
            <AccountButton setUser={setUser} />
          </div>
        </Toolbar>
        <Toolbar
          style={{
            backgroundColor: '#16a9e4',
            height: '30px',
            minHeight: '20px',
            color: 'whitesmoke',
          }}
        >
          <IconButton
            id="menuButton"
            style={{ color: 'inherit' }}
            onClick={handleSideBar}
            edge="start"
          >
            <Menu />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Joyride
        steps={joyrideSteps as JoyrideStep[]}
        run={runJoyride}
        continuous
        showSkipButton
        callback={handleJoyrideCallback}
        locale={{
          back: t('common:interaction.button.back'),
          close: t('common:interaction.button.close'),
          last: t('common:interaction.button.next'),
          next: t('common:interaction.button.next'),
          skip: t('common:interaction.button.skip'),
        }}
      />
    </React.Fragment>
  )
}
